import { createI18n } from 'vue-i18n'
import type { App } from 'vue'
import { DEFAULT_LOCALE, SUPPORTED_LOCALE_CODES } from '@/core/config/languages'

// Type for locale messages
export type LocaleMessages = {
  [key: string]: LocaleMessages | string
}

// Type for merged messages
export type Messages = {
  [locale: string]: LocaleMessages
}

// Detect browser language
function detectBrowserLanguage(): string {
  // Check if user has manually set a language
  const userSetLocale = localStorage.getItem('userSetLocale')
  if (userSetLocale) {
    return userSetLocale
  }

  // Otherwise, detect from browser
  const browserLang = navigator.language || navigator.languages?.[0] || DEFAULT_LOCALE

  // Extract the language code (e.g., 'fr-FR' -> 'fr')
  const langCode = browserLang.split('-')[0].toLowerCase()

  // Check if we support this language
  return SUPPORTED_LOCALE_CODES.includes(langCode) ? langCode : DEFAULT_LOCALE
}

// Create i18n instance with default configuration
export function createI18nInstance(messages: Messages) {
  // Get the appropriate locale
  const locale = detectBrowserLanguage()

  return createI18n({
    legacy: false, // Use Composition API mode
    locale, // Use detected or user-set locale
    fallbackLocale: DEFAULT_LOCALE, // Fallback locale
    messages,
    globalInjection: true, // Inject global properties
    missingWarn: false, // Disable missing translation warnings in production
    fallbackWarn: false, // Disable fallback warnings in production
  })
}

// Plugin installer
export function installI18n(app: App, messages: Messages) {
  const i18n = createI18nInstance(messages)
  app.use(i18n)
  return i18n
}
